<template>
  <div class="backsearch-page">
    <!-- Volume Counter (from /quota API) -->
    <div class="volume-banner" v-if="!loadingRequests">
      <template v-if="quotaLoading">
        <span class="volume-label">
          <i class="fas fa-chart-bar"></i>
          {{ $t('istarshine.backsearch.quota_loading') }}…
        </span>
      </template>
      <template v-else-if="quotaFetchFailed">
        <span class="quota-warning">
          <i class="fas fa-exclamation-circle"></i>
          {{ $t('istarshine.backsearch.quota_load_error') }}
        </span>
      </template>
      <template v-else>
        <span class="volume-label">
          <i class="fas fa-chart-bar"></i>
          {{ $t('istarshine.backsearch.volume_remaining') }}:
        </span>
        <strong :class="{ 'quota-danger': backsearchRemaining <= 0 }">{{ numberFmt(backsearchRemaining) }}</strong>
        <span class="volume-detail">
          ({{ numberFmt(backsearchProcessed) }} {{ $t('istarshine.backsearch.volume_processed') }} /
          {{ numberFmt(backsearchLimit) }} {{ $t('istarshine.backsearch.volume_limit') }})
        </span>
        <span v-if="backsearchRemaining <= 0" class="quota-warning">
          <i class="fas fa-exclamation-circle"></i>
          {{ $t('istarshine.backsearch.quota_reached') }}
        </span>
      </template>
    </div>

    <!-- Messages -->
    <div v-if="errorMessage" class="alert alert-error">
      <i class="fas fa-times-circle"></i> {{ errorMessage }}
      <button class="alert-close" @click="errorMessage = ''">&times;</button>
    </div>

    <!-- Loading -->
    <div v-if="loadingRequests" class="text-center py-3">
      <loader-inline />
    </div>

    <!-- Main layout: sidebar + form -->
    <div v-if="!loadingRequests" class="bs-layout">
      <!-- LEFT SIDEBAR -->
      <aside class="bs-sidebar">
        <div class="sidebar-list">
          <div class="sidebar-header">
            <h4 class="sidebar-title">{{ $t('istarshine.backsearch.title') }}</h4>
            <a class="action-button" :title="$t('istarshine.backsearch.new_request')" @click.prevent="onNewRequest">
              <i class="far fa-plus-circle"></i>
            </a>
          </div>

          <div v-if="filteredRequests.length === 0" class="sidebar-empty">
            {{ searchQuery ? 'No matching requests' : $t('istarshine.backsearch.no_requests') }}
          </div>

          <div class="sidebar-items">
            <div
              v-for="req in filteredRequests"
              :key="req.id"
              class="sidebar-item"
              :class="{ active: isEditing && editingRequest && editingRequest.id === req.id }"
              @click="onSidebarSelect(req)"
            >
              <div class="sidebar-item-primary">
                <span class="sidebar-status-dot" :class="sidebarStatusClass(req)"></span>
                <div class="sidebar-item-query">{{ sidebarPrimaryLine(req) }}</div>
              </div>
              <div class="sidebar-item-secondary">{{ sidebarMetaLine(req) }}</div>
            </div>
          </div>
        </div>
      </aside>

      <!-- RIGHT MAIN: Form -->
      <section class="bs-main">
        <backsearch-form
          v-if="showEditorForm"
          :request="editingRequest"
          :disabled="formDisabled"
          :saving="saving"
          :backsearch-remaining="backsearchRemainingForForm"
          :seed-form="seedForm"
          :available-providers="availableProviders"
          :copy-from-options="copyFromOptions"
          :current-copy-option="selectedCopyOption"
          @copy-from-select="onCopyFromSelect"
          @save="onSave"
          @cancel="goBack"
        />
        <div v-else-if="loadingEditor" class="text-center py-3">
          <loader-inline />
        </div>
        <div v-else-if="isEditing" class="text-center py-3 text-muted"></div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import BacksearchForm from '../../components/istarshine/BacksearchForm.vue';
import loaderInline from '../../components/loader-inline.vue';
import {
  mapSearchConfigToFormFields,
  mapBacksearchToFormFields
} from '../../helpers/istarshine/backsearchTemplate';
import { maxString } from '../../helpers/string';
const BACKSEARCH_PATH = 'searches/asian_socials_backsearch_requests';
const CONFIG_PATH = 'searches/asian_socials_config';

function parseBacksearchQuotaBody(responseBody) {
  if (!responseBody || typeof responseBody !== 'object') return null;
  const raw = responseBody.data && typeof responseBody.data === 'object'
    ? responseBody.data
    : responseBody;
  if (raw === null || raw === undefined || raw.quota === undefined) return null;
  return {
    quota: Number(raw.quota),
    used: Number(raw.used),
    remaining: Number(raw.remaining)
  };
}

const numberFormat = new Intl.NumberFormat('en');

export default {
  name: 'backsearch-create',
  components: { BacksearchForm, loaderInline },

  props: {
    projectSettings: { type: Object, default: null }
  },

  data() {
    return {
      editingRequest: null,
      saving: false,
      errorMessage: '',
      requests: [],
      loadingRequests: true,
      loadingEditor: false,
      searchQuery: '',
      quotaSnapshot: null,
      quotaLoading: true,
      quotaFetchFailed: false,
      // Copy-from: Istarshine search config rows loaded for the dropdown
      searchConfigs: [],
      // Copy-from: the option the user has currently selected in the dropdown
      selectedCopyOption: null
    };
  },

  computed: {
    ...mapState({
      project_id: state => state.booleanSearch.project_id
    }),

    backsearchLimit() {
      return this.quotaSnapshot ? this.quotaSnapshot.quota : 0;
    },

    backsearchProcessed() {
      return this.quotaSnapshot ? this.quotaSnapshot.used : 0;
    },

    backsearchRemaining() {
      return this.quotaSnapshot ? Math.max(0, this.quotaSnapshot.remaining) : 0;
    },

    /** Pass to form only when quota loaded (hides inline line while loading). */
    backsearchRemainingForForm() {
      if (this.quotaLoading || !this.quotaSnapshot) return null;
      return Math.max(0, this.quotaSnapshot.remaining);
    },

    filteredRequests() {
      if (!this.searchQuery) return this.requests;
      const q = this.searchQuery.toLowerCase().trim();
      return this.requests.filter(r =>
        (r.query || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.status || '').toLowerCase().includes(q)
      );
    },

    requestId() {
      return this.$route.params.request_id || null;
    },

    isEditing() {
      return !!this.requestId;
    },

    formDisabled() {
      if (!this.editingRequest || !this.editingRequest.id) return false;
      return this.normalizedStatus(this.editingRequest.status) !== 'draft';
    },

    showEditorForm() {
      if (!this.isEditing) return true;
      return !!this.editingRequest && !this.loadingEditor;
    },

    // Builds the grouped option list for the "Copy settings from" multiselect.
    // Group 1 — search configs (asian_socials_config rows): query + provider presets.
    // Group 2 — previous backsearch requests: previously run searches.
    // Both groups share the same option shape: { value, label, source, kind }.
    copyFromOptions() {
      const groups = [];
      if (this.searchConfigs.length) {
        groups.push({
          group: this.$t('istarshine.backsearch.copy_from_group_config'),
          items: this.searchConfigs
            .filter(row => row && row.id !== null && row.id !== undefined)
            .map(row => {
              const providerLabel = row.provider
                ? (this.$t(`istarshine.${row.provider}`) || row.provider)
                : this.$t('istarshine.backsearch.all_providers');
              return {
                value: `config:${row.id}`,
                label: `${providerLabel}: ${maxString(row.query || '', 60, true)}`,
                source: row,
                kind: 'config'
              };
            })
        });
      }
      if (this.requests.length) {
        groups.push({
          group: this.$t('istarshine.backsearch.copy_from_group_backsearch'),
          items: this.requests
            .filter(req => req && req.id !== null && req.id !== undefined)
            .map(req => {
              const name = req.name !== null && req.name !== undefined && String(req.name).trim();
              return {
                value: `backsearch:${req.id}`,
                label: name || maxString(req.query || `ID: ${req.id}`, 60, true),
                source: req,
                kind: 'backsearch'
              };
            })
        });
      }
      return groups;
    },

    // Converts the selected copy-from option into a complete form-fields object.
    // BacksearchForm watches this prop and stamps the values into its form when it changes.
    // Returning null means "no copy selected" — the form stays blank.
    seedForm() {
      if (!this.selectedCopyOption) return null;
      const { source, kind } = this.selectedCopyOption;
      const mpc = this.backsearchRemainingForForm || 5000;
      return kind === 'config'
        ? mapSearchConfigToFormFields(source, mpc)
        : mapBacksearchToFormFields(source, mpc);
    },

    // Providers enabled for this project, sourced from project settings loaded by
    // generic-projects.vue and passed down as a prop. Falls back to empty array so
    // the form renders safely before settings arrive.
    availableProviders() {
      if (!this.projectSettings?.enabled_asian_social_providers) {
        return [];
      }
      return this.projectSettings.enabled_asian_social_providers;
    }
  },

  created() {
    this.fetchRequests();
    this.fetchQuota();
    this.fetchConfig();
  },

  watch: {
    requestId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.loadRequest();
        } else {
          this.editingRequest = null;
          this.loadingEditor = false;
          this.errorMessage = '';
        }
      }
    }
  },

  methods: {

    numberFmt(n) {
      return n !== null && n !== undefined ? numberFormat.format(n) : '0';
    },

    statusLabel(status) {
      if (!status) return this.$t('istarshine.backsearch.status_null');
      return this.$t('istarshine.backsearch.status_' + status);
    },

    formatDate(d) {
      if (!d) return '';
      return String(d).substring(0, 10);
    },

    sidebarPrimaryLine(req) {
      const n = req.name !== null && req.name !== undefined && String(req.name).trim();
      if (n) return n;
      return req.query || '';
    },

    normalizedStatus(status) {
      return status || 'draft';
    },

    sidebarStatusClass(req) {
      return `is-${this.normalizedStatus(req.status)}`;
    },

    sidebarMetaLine(req) {
      const completedDate = req.completed_at || req.updated_at || req.created_at;
      if (this.normalizedStatus(req.status) === 'completed' && completedDate) {
        return this.$t('istarshine.backsearch.completed_on', { date: this.formatDate(completedDate) });
      }

      const createdDate = req.created_at || req.updated_at;
      if (createdDate) {
        return this.$t('istarshine.backsearch.created_on', { date: this.formatDate(createdDate) });
      }

      return this.statusLabel(req.status);
    },

    isEditableRequest(req) {
      return this.normalizedStatus(req.status) === 'draft';
    },

    basePath() {
      return `projects/${this.project_id}/${BACKSEARCH_PATH}`;
    },

    async fetchQuota() {
      this.quotaLoading = true;
      this.quotaFetchFailed = false;
      try {
        const { data } = await axios.get(`projects/${this.project_id}/${BACKSEARCH_PATH}/quota`);
        const row = parseBacksearchQuotaBody(data);
        this.quotaSnapshot = row;
        if (!row) this.quotaFetchFailed = true;
      } catch (err) {
        console.error('Error fetching backsearch quota:', err);
        this.quotaSnapshot = null;
        this.quotaFetchFailed = true;
      } finally {
        this.quotaLoading = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'clients--client_id-projects--project_id-istarshine-backsearch' });
    },

    onNewRequest() {
      this.editingRequest = null;
      this.$router.push({
        name: 'clients--client_id-projects--project_id-istarshine-backsearch-create'
      });
    },

    onSidebarSelect(req) {
      this.$router.push({
        name: 'clients--client_id-projects--project_id-istarshine-backsearch-edit',
        params: { request_id: req.id }
      });
    },

    /**
     * API may return { message, error } on 400; surface readable text and map known quota errors.
     */
    backsearchSaveErrorMessage(err) {
      const data = err.response && err.response.data;
      const fallback = this.$t('istarshine.backsearch.save_error');
      if (!data) return fallback;

      let raw = '';
      if (typeof data === 'string') {
        raw = data.trim();
      } else {
        const m = data.message ?? data.error;
        raw = typeof m === 'string' ? m.trim() : '';
      }
      if (!raw) return fallback;

      const lower = raw.toLowerCase();
      if (
        (lower.includes('max_post_count') || lower.includes('post count')) &&
        (lower.includes('quota') || lower.includes('remaining backsearch'))
      ) {
        return this.$t('istarshine.backsearch.quota_exceeded');
      }

      return raw;
    },

    async fetchRequests() {
      this.loadingRequests = true;
      try {
        const { data } = await axios.get(this.basePath());
        this.requests = Array.isArray(data.data) ? data.data : [];
      } catch (err) {
        console.error('Error fetching backsearch requests:', err);
        this.requests = [];
      } finally {
        this.loadingRequests = false;
      }
    },

    // Receives the user's dropdown selection from BacksearchForm via the
    // copy-from-select event. Updating selectedCopyOption triggers the seedForm
    // computed, which in turn updates the form fields in BacksearchForm.
    onCopyFromSelect(option) {
      this.selectedCopyOption = option || null;
    },

    // Loads the project's Istarshine search config rows to populate the
    // "Search configuration" group in the copy-from dropdown.
    async fetchConfig() {
      try {
        const { data } = await axios.get(`projects/${this.project_id}/${CONFIG_PATH}`);
        this.searchConfigs = Array.isArray(data.data) ? data.data : [];
      } catch (err) {
        console.error('Error fetching istarshine config:', err);
        this.searchConfigs = [];
      }
    },

    async loadRequest() {
      const id = this.requestId;
      if (!id) {
        this.editingRequest = null;
        this.loadingEditor = false;
        return;
      }
      this.errorMessage = '';
      this.editingRequest = null;
      this.loadingEditor = true;
      try {
        const { data } = await axios.get(`${this.basePath()}/${id}`);
        if (id !== this.requestId) return;
        this.editingRequest = data.data || data;
      } catch (err) {
        if (id !== this.requestId) return;
        console.error('Error loading request:', err);
        this.errorMessage = err.response?.data?.error || 'Error loading request';
        this.editingRequest = null;
      } finally {
        if (id === this.requestId) {
          this.loadingEditor = false;
        }
      }
    },

    async onSave(payload) {
      if (this.formDisabled) return;
      this.saving = true;
      this.errorMessage = '';
      try {
        if (this.isEditing) {
          await axios.put(`${this.basePath()}/${this.requestId}`, payload);
        } else {
          await axios.post(this.basePath(), payload);
        }
        this.goBack();
      } catch (err) {
        this.$notify({
          group: 'toast_top_right',
          type: 'error',
          text: this.backsearchSaveErrorMessage(err)
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/base/variables.scss";

.backsearch-page {
  padding: 0 1rem;
  --bs-sticky-top: calc(60px + 3.5rem);
  --bs-sidebar-max-height: calc(100vh - var(--bs-sticky-top) - 1rem);
}

.volume-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.75rem 1rem;
  background: $gray-100;
  border: 1px solid $gray-200;
  border-radius: 6px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

.volume-label {
  font-weight: 500;
  i { margin-right: 0.3rem; color: $carma-teal; }
}

.volume-detail {
  color: $gray-500;
  font-size: 0.85rem;
}

.quota-danger { color: $red; }

.quota-warning {
  color: $red;
  font-size: 0.85rem;
  margin-left: 0.5rem;
  i { margin-right: 0.2rem; }
}

.alert {
  padding: 0.65rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.alert-error {
  background: lighten($red, 42%);
  border: 1px solid lighten($red, 28%);
  color: darken($red, 10%);
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  color: inherit;
}

/* ── Sidebar + Main flex layout ── */
.bs-layout {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.bs-sidebar {
  min-width: 17.15rem;
  width: 30%;
  flex: 0 0 30%;
  align-self: flex-start;
  height: var(--bs-sidebar-max-height);
  position: sticky;
  top: var(--bs-sticky-top);
  z-index: 1;
  overflow: hidden;
}

.bs-main {
  flex: 1 1 70%;
  max-width: 70%;
  padding: 0 0 1rem 1rem;
}

/* ── Sidebar list ── */
.sidebar-list {
  background: $white;
  border: 1px solid $gray-200;
  border-radius: 6px;
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid $gray-200;
}

.sidebar-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.action-button {
  cursor: pointer;
  i {
    color: $gray-500;
    font-size: 1.2rem;
    transition: color 0.25s ease-in;
  }
  &:hover i { color: $orange; }
}

/* ── Sidebar items scrollable ── */
.sidebar-items {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-color: $gray-300 $gray-100;
  scrollbar-width: thin;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: $gray-300; border-radius: 3px; }
  &::-webkit-scrollbar-thumb:hover { background: $gray-400; }
}

.sidebar-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  color: $gray-400;
  font-size: 0.875rem;
}

.sidebar-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid $gray-100;
  transition: background 0.12s;

  &:last-child { border-bottom: none; }
  &:hover { background: lighten($gray-100, 2%); }
  &.active {
    background: $gray-100;
  }
}

.sidebar-item-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}

.sidebar-status-dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.is-draft { background: $gray-300; }
  &.is-queued { background: #f59e0b; }
  &.is-processing { background: $carma-teal; }
  &.is-completed { background: #10b981; }
  &.is-errored { background: $red; }
}

.sidebar-item-query {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: $gray-700;
}

.sidebar-item-secondary {
  font-size: 0.75rem;
  color: $gray-400;
}

.py-3 { padding: 1.5rem 0; }
</style>
